import { useMemo } from "react";
import { cn } from "../lib/utils";
import { Checkbox } from "../components/ui/Checkbox";
import { HugeiconsIcon } from "@hugeicons/react";
import { Target02Icon, Settings01Icon, UserGroupIcon } from "@hugeicons/core-free-icons";
import { IconClipboardCheck } from "nucleo-glass";
import { NucleoIcon } from "../components/ui/NucleoIcon";
import type { Card, CardStatus, LaneId } from "../data/types";
import { getTaskMeta, sortCardsByExecution } from "../lib/taskMeta";
import type { IconSvgElement } from "@hugeicons/react";

interface ChecklistPageProps {
  cards: Card[];
  onToggleCard: (id: string, status: CardStatus) => void;
}

const sections: Array<{ key: Card["owner"] | "Joint"; label: string; icon: IconSvgElement }> = [
  { key: "Joel", label: "Joel — Sales", icon: Target02Icon },
  { key: "Albert", label: "Albert — Product", icon: Settings01Icon },
  { key: "Joint", label: "Joint — EOD", icon: UserGroupIcon },
];

const LANE_ORDER: LaneId[] = [
  "commercial_pipeline", "channels", "weekly_content",
  "revops_tracking", "feedback_loop", "objective_kpis", "offer_pricing",
];

const laneDisplayLabels: Record<string, string> = {
  commercial_pipeline: "Pipeline",
  channels: "Channels",
  weekly_content: "Content",
  revops_tracking: "RevOps",
  feedback_loop: "Feedback",
  objective_kpis: "KPIs",
  offer_pricing: "Pricing",
};

export function ChecklistPage({ cards, onToggleCard }: ChecklistPageProps) {
  const todayIso = new Date().toISOString().slice(0, 10);

  const dailyCards = useMemo(() => {
    const todayRecurring = cards.filter((card) => {
      const meta = getTaskMeta(card);
      return meta.taskType === "recurring_daily" && card.targetDate === todayIso;
    });

    if (todayRecurring.length > 0) {
      return sortCardsByExecution(todayRecurring);
    }

    const fallback = cards.filter((card) => getTaskMeta(card).taskType === "recurring_daily");
    return sortCardsByExecution(fallback);
  }, [cards, todayIso]);

  const checkedCount = dailyCards.filter((card) => card.status === "done").length;
  const progressPct = dailyCards.length > 0 ? Math.round((checkedCount / dailyCards.length) * 100) : 0;

  // Two-level grouping: owner → lane
  const groupedByOwner = useMemo(() => {
    return sections.map((section) => {
      const ownerCards = dailyCards.filter((c) => c.owner === section.key);
      if (ownerCards.length === 0) return null;

      const laneMap = new Map<string, Card[]>();
      for (const card of ownerCards) {
        const bucket = laneMap.get(card.laneId) ?? [];
        bucket.push(card);
        laneMap.set(card.laneId, bucket);
      }

      const lanes = LANE_ORDER
        .filter((laneId) => laneMap.has(laneId))
        .map((laneId) => {
          const laneCards = laneMap.get(laneId) ?? [];
          return {
            laneId,
            laneLabel: laneDisplayLabels[laneId] ?? laneId,
            cards: laneCards,
            doneCount: laneCards.filter((c) => c.status === "done").length,
          };
        });

      return {
        key: section.key,
        label: section.label,
        icon: section.icon,
        lanes,
        totalCount: ownerCards.length,
        doneCount: ownerCards.filter((c) => c.status === "done").length,
      };
    }).filter(Boolean) as Array<{
      key: Card["owner"];
      label: string;
      icon: IconSvgElement;
      lanes: Array<{ laneId: string; laneLabel: string; cards: Card[]; doneCount: number }>;
      totalCount: number;
      doneCount: number;
    }>;
  }, [dailyCards]);

  const dateStr = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="w-full px-[var(--spacing-4)] py-[var(--spacing-6)]">
        <div className="mb-[var(--spacing-8)]">
          <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-2)]">
            <NucleoIcon icon={<IconClipboardCheck size={18} />} className="text-[var(--color-accent-300)]" />
            <h2 className="text-[14px] font-semibold text-[var(--color-text-primary)]">{dateStr}</h2>
          </div>
          <div className="flex items-center gap-[var(--spacing-3)]">
            <div className="flex-1 h-[6px] rounded-full bg-[var(--color-border-stroke)] overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full animate-progress-fill",
                  progressPct >= 100
                    ? "bg-[var(--color-state-success)]"
                    : progressPct >= 80
                      ? "bg-[var(--color-accent)]"
                      : "bg-[var(--color-accent-400)]",
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <span className="text-[14px] tabular-nums text-[var(--color-text-secondary)] shrink-0">
              {checkedCount}/{dailyCards.length} · {progressPct}%
            </span>
          </div>
        </div>

        {dailyCards.length === 0 ? (
          <div className="rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)] px-[var(--spacing-4)] py-[var(--spacing-6)] text-[14px] text-[var(--color-text-muted)]">
            No recurring daily tasks loaded. Import `cards_master_v2.csv` to activate Daily Ops Checklist.
          </div>
        ) : (
          <div className="space-y-[var(--spacing-6)]">
            {groupedByOwner.map((ownerGroup) => (
              <div key={ownerGroup.key}>
                {/* Owner header */}
                <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-3)]">
                  <HugeiconsIcon icon={ownerGroup.icon} size={16} className="text-[var(--color-text-muted)]" />
                  <h3 className="text-[14px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.04em]">
                    {ownerGroup.label}
                  </h3>
                  <span className="text-[14px] tabular-nums text-[var(--color-text-muted)] ml-auto">
                    {ownerGroup.doneCount}/{ownerGroup.totalCount}
                  </span>
                </div>

                {/* Lane sub-groups */}
                <div className="space-y-[var(--spacing-4)]">
                  {ownerGroup.lanes.map((lane) => {
                    const lanePct = lane.cards.length > 0 ? Math.round((lane.doneCount / lane.cards.length) * 100) : 0;
                    return (
                      <div key={lane.laneId}>
                        {/* Lane header with mini progress */}
                        <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-1)] px-[var(--spacing-3)]">
                          <span className="text-[12px] font-medium text-[var(--color-text-muted)] uppercase tracking-[0.04em]">
                            {lane.laneLabel}
                          </span>
                          <div className="flex-1 h-[3px] rounded-full bg-[var(--color-border-stroke)] overflow-hidden max-w-[80px]">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                lanePct >= 100 ? "bg-[var(--color-state-success)]" : "bg-[var(--color-accent-400)]",
                              )}
                              style={{ width: `${lanePct}%` }}
                            />
                          </div>
                          <span className="text-[11px] tabular-nums text-[var(--color-text-muted)]">
                            {lane.doneCount}/{lane.cards.length}
                          </span>
                        </div>

                        {/* Cards */}
                        <div className="space-y-[var(--spacing-0_5)]">
                          {lane.cards.map((card, index) => {
                            const meta = getTaskMeta(card);
                            return (
                              <label
                                key={card.id}
                                htmlFor={`daily-${card.id}`}
                                className={cn(
                                  "flex items-start gap-[var(--spacing-3)] rounded-[var(--radius-md)] px-[var(--spacing-3)] py-[var(--spacing-3)]",
                                  "cursor-pointer transition-all duration-[var(--duration-fast)] hover:bg-[var(--color-bg-hover)] border border-transparent",
                                  "animate-list-item-in",
                                  card.status === "done" && "opacity-50",
                                )}
                                style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
                              >
                                <Checkbox
                                  id={`daily-${card.id}`}
                                  checked={card.status === "done"}
                                  onCheckedChange={(checked) => onToggleCard(card.id, checked ? "done" : "planned")}
                                  className="mt-[1px]"
                                />
                                <div className="flex-1 min-w-0">
                                  <span
                                    className={cn(
                                      "text-[14px] leading-[20px]",
                                      card.status === "done"
                                        ? "text-[var(--color-text-muted)] line-through"
                                        : "text-[var(--color-text-primary)]",
                                    )}
                                  >
                                    {card.title}
                                  </span>
                                  <div className="flex items-center gap-[var(--spacing-2)] mt-[var(--spacing-1)] text-[11px] text-[var(--color-text-muted)]">
                                    {meta.executionWindow && (
                                      <span className="border border-[var(--color-border-stroke)] rounded-[var(--radius-sm)] px-[6px] py-[2px]">
                                        {meta.executionWindow.replace("_CET", " CET")}
                                      </span>
                                    )}
                                    {meta.sla && (
                                      <span className="border border-[var(--color-border-stroke)] rounded-[var(--radius-sm)] px-[6px] py-[2px]">
                                        SLA {meta.sla}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
